import { router, useLocalSearchParams } from 'expo-router';
import { useIntl } from 'react-intl';

import { tokens } from '@fhs/ui';
import { useServiceModeContext } from '@fhs-legacy/frontend/src/state/service-mode';
import { ILocation } from '@rbi-ctg/frontend';
import { useStoreClosedActionSheet } from 'components/store-closed-action-sheet';
import { IRestaurantNode } from 'generated/rbi-graphql';
import { CustomEventNames, EventTypes, useCRMEventsContext } from 'state/crm-events';
import { useLocale } from 'state/intl';
import { ServiceMode, useOrderContext } from 'state/order';
import { Geography } from 'utils/rbi-common';
import { readableDistanceFromStore } from 'utils/restaurant';
import { routes } from 'utils/routing';
import { isCatering } from 'utils/service-mode';

import { AddressBoxSuggestion } from './address-box-suggestion';
import { AddressBoxAddress, BorderedContainer, Circle } from './delivery-unavailable.styled';

export type AddressBoxProps = {
  icon: React.ReactElement;
  address?: string;
  restaurant?: IRestaurantNode;
  activeCoordinates?: ILocation;
  isPickupAvailable?: boolean;
};

export const AddressBox: React.FC<React.PropsWithChildren<AddressBoxProps>> = ({
  icon,
  address,
  restaurant,
  activeCoordinates,
}) => {
  const params = useLocalSearchParams<{ back?: string }>();
  const { formatMessage } = useIntl();
  const { region } = useLocale();
  const { selectStore, selectServiceMode } = useOrderContext();
  const { logRBIEvent } = useCRMEventsContext();
  const { serviceMode } = useServiceModeContext();

  const distance = Geography.coordinateDistance(
    { latitude: activeCoordinates?.lat ?? 0, longitude: activeCoordinates?.lng ?? 0 },
    {
      latitude: restaurant?.latitude ?? 0,
      longitude: restaurant?.longitude ?? 0,
    }
  );
  const distanceText = readableDistanceFromStore(distance, region, formatMessage);

  const { requestScheduleFutureOrderingConfirmation, storeClosedActionSheet } =
    useStoreClosedActionSheet({
      restaurant: (restaurant || {}) as IRestaurantNode,
      isCatering: isCatering(serviceMode),
    });

  const pickStore = async () => {
    if (!restaurant) {
      return;
    }

    const shouldScheduleFuture = await requestScheduleFutureOrderingConfirmation();

    if (!shouldScheduleFuture) {
      return;
    }

    await selectStore(
      restaurant,
      () => {
        logRBIEvent({
          name: CustomEventNames.DELIVERY_UNAVAILABLE_SELECT_PICKUP_STORE,
          type: EventTypes.Navigation,
        });
        selectServiceMode(ServiceMode.TAKEOUT);
        router.replace(params.back ?? routes.menu);
      },
      ServiceMode.TAKEOUT
    );
  };

  return (
    <BorderedContainer>
      <Circle backgroundColor={tokens.colors.$blackOpacity04}>{icon}</Circle>
      {restaurant ? (
        <AddressBoxSuggestion
          pickStoreAction={pickStore}
          distance={distanceText}
          restaurant={restaurant}
        />
      ) : (
        <AddressBoxAddress fontSize="md">{address}</AddressBoxAddress>
      )}
      {restaurant && storeClosedActionSheet}
    </BorderedContainer>
  );
};
